import React from 'react';
import { Settings, ChevronDown, ChevronRight, Users, Calendar, X, AlertTriangle, Check, CheckCircle, Trash2, Edit2, LayoutGrid, Plus } from 'lucide-react';
import { format } from 'date-fns';

const Timeline = ({
  activeTurbines,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  toggleTurbineCollapse,
  collapsedTurbines,
  getAssignmentsForTurbine,
  taskCategories,
  setExpandedTasks,
  setSelectedTaskForPeopleFilter,
  setQuickAddTurbineId,
  setQuickAddStep,
  setQuickAddSelectedTasks,
  setQuickAddPeople,
  setQuickAddCar,
  setQuickAddPeopleSearch,
  setQuickAddTaskSearch,
  setShowQuickAddModal,
  expandedTasks,
  showTimeSliders,
  setShowTimeSliders,
  getIndividualTimes,
  timeToMinutes,
  minutesToTime,
  resizeTaskForPerson,
  loadTaskAssignments,
  allPeople,
  projectCars,
  unmarkTaskCompleted,
  markTaskCompleted,
  setTurbineAssignments,
  API_BASE_URL,
  axios,
  selectedProject,
  setTaskAssignData,
  setSelectedTaskForAssign,
  setSelectedTurbineForAssign,
  setEditingAssignmentId,
  setShowTaskAssignModal,
  setShowProjectOverview,
  loadAllProjectAssignments,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-steel-200">
      <div className="p-4 border-b border-steel-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center">
              <Settings className="w-5 h-5 mr-2 text-brand-600" />
              Active Turbines ({activeTurbines.length})
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              {activeTurbines.length > 0 ? 'Selected turbines for work planning' : 'Select turbines above to start planning'}
            </p>
          </div>
          <button
            onClick={() => setShowProjectOverview(true)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 transition-colors"
            title="View Project Overview"
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="text-sm font-medium">Project Overview</span>
          </button>
        </div>
      </div>

      <div className="p-4 space-y-2">
        {activeTurbines.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Settings className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>No turbines selected</p>
            <p className="text-xs text-slate-400 mt-1">
              {activeTurbines.length > 0 ? 'Select turbines above to start planning' : 'Check project settings'}
            </p>
          </div>
        ) : activeTurbines.map(turbine => (
            <div
              key={turbine.id}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, turbine.id)}
              onClick={() => toggleTurbineCollapse(turbine.id)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                setQuickAddTurbineId(turbine.id);
                setQuickAddStep(1);
                setQuickAddSelectedTasks([]);
                setQuickAddPeople([]);
                setQuickAddCar(null);
                setQuickAddPeopleSearch('');
                setQuickAddTaskSearch('');
                setShowQuickAddModal(true);
              }}
              className="border-2 border-dashed border-steel-300 rounded-xl p-2 min-h-[40px] bg-white hover:border-steel-400 transition-colors shadow-sm cursor-pointer"
              title={collapsedTurbines.has(turbine.id) ? "Double-click for quick add" : "Double-click for quick add"}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-900 text-sm">
                  T{turbine.turbine_number || turbine.id}
                  {turbine.turbine_id && <span className="text-slate-500 font-normal ml-1">({turbine.turbine_id})</span>}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setQuickAddTurbineId(turbine.id);
                      setQuickAddStep(1);
                      setQuickAddSelectedTasks([]);
                      setQuickAddPeople([]);
                      setQuickAddCar(null);
                      setQuickAddPeopleSearch('');
                      setQuickAddTaskSearch('');
                      setShowQuickAddModal(true);
                    }}
                    className="p-1 bg-brand-600 text-white rounded hover:bg-brand-700 transition-colors"
                    title="Add Task"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <ChevronDown className={`w-4 h-4 transition-transform text-slate-400 ${collapsedTurbines.has(turbine.id) ? 'rotate-[-90deg]' : ''}`} />
                </div>
              </div>

              {!collapsedTurbines.has(turbine.id) && (
                <div className="mt-3 pt-2 border-t border-steel-200">
                  {getAssignmentsForTurbine(turbine.id).length === 0 ? (
                    <div className="text-xs text-slate-600">
                      No tasks assigned
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                    {getAssignmentsForTurbine(turbine.id).map(assignment => {
                      const hasNoPeople = !assignment.assignedPeople || assignment.assignedPeople.length === 0;
                      return (
                      <div key={assignment.id} className={`rounded p-2 text-xs cursor-pointer hover:shadow-md transition-all ${assignment.is_completed ? 'opacity-60 bg-steel-100' : ''} ${assignment.isAlternative ? 'border-2 border-dashed border-amber-400' : ''} ${hasNoPeople && !assignment.is_completed ? 'border-2 border-red-400 ring-2 ring-red-200' : ''}`} style={{
                        backgroundColor: assignment.is_completed ? '#f3f4f6' : hasNoPeople ? '#FEE2E2' : assignment.isAlternative ? '#FEF3C7' : (assignment.categoryColor ? `${assignment.categoryColor}20` : '#DBEAFE')
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedTasks(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(assignment.id)) {
                            newSet.delete(assignment.id);
                            setSelectedTaskForPeopleFilter(null);
                          } else {
                            newSet.add(assignment.id);
                            setSelectedTaskForPeopleFilter(assignment);
                          }
                          return newSet;
                        });
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        const category = taskCategories.find(cat => cat.name === assignment.category);
                        const subcategory = category?.subcategories.find(sub => sub.name === assignment.subcategory);

                        if (category && subcategory) {
                          setSelectedTaskForAssign({
                            task: { name: category.name, color: category.color },
                            subcategory: subcategory
                          });
                          setSelectedTurbineForAssign(assignment.turbineId);
                          setEditingAssignmentId(assignment.id);
                          // Convert individual_times from backend format to frontend format
                          const individualTimes = {};
                          if (assignment.individual_times) {
                            Object.keys(assignment.individual_times).forEach(personId => {
                              individualTimes[personId] = {
                                startTime: assignment.individual_times[personId].start_time || assignment.startTime,
                                endTime: assignment.individual_times[personId].end_time || assignment.endTime
                              };
                            });
                          }
                          setTaskAssignData({
                            startTime: assignment.startTime,
                            endTime: assignment.endTime,
                            peopleNeeded: assignment.peopleNeeded,
                            assignedPeople: assignment.assignedPeople || [],
                            assignedCar: assignment.assignedCar,
                            isAlternative: assignment.isAlternative || false,
                            individualTimes: individualTimes
                          });
                          setShowTaskAssignModal(true);
                        }
                      }}>
                        {/* 3-column layout: Task+Time | People | Actions */}
                        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start">
                          {/* Column 1: Task name and time */}
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate flex items-center gap-1">
                              {assignment.isAlternative && (
                                <span className="text-amber-600 text-xs bg-amber-100 px-1 rounded flex-shrink-0" title="Alternative task">
                                  ALT
                                </span>
                              )}
                              <span className="truncate">{assignment.subcategory}</span>
                            </div>
                            <div className="text-slate-500 text-xs truncate">{assignment.category}</div>
                            <div
                              className="text-slate-700 font-medium cursor-pointer hover:bg-brand-100 px-1 py-0.5 rounded inline-block mt-0.5"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowTimeSliders(showTimeSliders === assignment.id ? null : assignment.id);
                              }}
                              title="Click to adjust time"
                            >
                              {(() => {
                                const allTimes = assignment.assignedPeople.map(personId => {
                                  const { startTime, endTime } = getIndividualTimes(assignment, personId);
                                  return { startTime, endTime };
                                });

                                if (allTimes.length === 0) {
                                  return `${assignment.startTime} - ${assignment.endTime}`;
                                }

                                const earliestStart = allTimes.reduce((earliest, time) =>
                                  timeToMinutes(time.startTime) < timeToMinutes(earliest) ? time.startTime : earliest,
                                  allTimes[0].startTime
                                );

                                const latestEnd = allTimes.reduce((latest, time) =>
                                  timeToMinutes(time.endTime) > timeToMinutes(latest) ? time.endTime : latest,
                                  allTimes[0].endTime
                                );

                                const allSame = allTimes.every(time =>
                                  time.startTime === allTimes[0].startTime && time.endTime === allTimes[0].endTime
                                );

                                if (allSame) {
                                  return `${allTimes[0].startTime} - ${allTimes[0].endTime}`;
                                } else {
                                  return `${earliestStart} - ${latestEnd} (var)`;
                                }
                              })()}
                            </div>
                            {assignment.assignedCar && (
                              <div className="text-slate-500 text-xs mt-0.5 truncate">
                                🚗 {(() => {
                                  const car = projectCars.find(c => c.id === assignment.assignedCar);
                                  return car ? `${car.plate_number}` : `ID: ${assignment.assignedCar}`;
                                })()}
                              </div>
                            )}
                          </div>

                          {/* Column 2: People list */}
                          <div className="min-w-0 border-l border-steel-300 pl-2">
                            {assignment.assignedPeople && assignment.assignedPeople.length > 0 ? (
                              <div className="space-y-0.5">
                                {assignment.assignedPeople.map((personId) => {
                                  const person = allPeople.find(p => p.id === personId);
                                  const name = person ? `${person.last_name} ${person.first_name}` : `User ${personId}`;
                                  return (
                                    <div key={personId} className="flex items-center gap-1 text-slate-700 truncate">
                                      <Users className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                      <span className="truncate font-semibold">{name}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-red-600">
                                <AlertTriangle className="w-3 h-3" />
                                <span className="font-medium text-xs">No people assigned!</span>
                              </div>
                            )}
                          </div>

                          {/* Column 3: Actions */}
                          <div className="flex flex-col items-center gap-1 border-l border-steel-300 pl-2">
                            {assignment.is_completed ? (
                              <button
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  await unmarkTaskCompleted(assignment.id);
                                  if (loadAllProjectAssignments) {
                                    loadAllProjectAssignments();
                                  }
                                }}
                                className="flex items-center gap-1 text-green-700 bg-green-100 hover:bg-green-200 rounded px-2 py-1"
                                title="Click to remove approval"
                              >
                                <CheckCircle className="w-4 h-4" />
                                <span className="text-xs">Done</span>
                              </button>
                            ) : (
                              <button
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  await markTaskCompleted(assignment.id);
                                  if (loadAllProjectAssignments) {
                                    loadAllProjectAssignments();
                                  }
                                }}
                                className="flex items-center gap-1 text-orange-700 bg-orange-100 hover:bg-orange-200 rounded px-2 py-1"
                                title="Click to approve"
                              >
                                <AlertTriangle className="w-4 h-4" />
                                <span className="text-xs">Pending</span>
                              </button>
                            )}
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const confirmDelete = window.confirm(
                                  `Delete assignment?\n\n${assignment.subcategory}\n${assignment.startTime} - ${assignment.endTime}`
                                );

                                if (!confirmDelete) return;

                                const token = localStorage.getItem('access_token');
                                const headers = token ? { Authorization: `Bearer ${token}` } : {};

                                try {
                                  await axios.delete(`${API_BASE_URL}/api/task-assignments/${assignment.id}/`, { headers });
                                  setTurbineAssignments(prev => ({
                                    ...prev,
                                    [turbine.id]: prev[turbine.id].filter(a => a.id !== assignment.id)
                                  }));
                                  // Refresh experience data
                                  if (loadAllProjectAssignments) {
                                    loadAllProjectAssignments();
                                  }
                                } catch (error) {
                                  console.error('Error deleting assignment:', error);
                                  alert('Error deleting assignment.');
                                }
                              }}
                              className="flex items-center gap-1 text-red-700 bg-red-100 hover:bg-red-200 rounded px-2 py-1"
                              title="Delete assignment"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="text-xs">Delete</span>
                            </button>
                          </div>
                        </div>
                        {showTimeSliders === assignment.id && (
                          <div className="mt-3 p-3 bg-brand-50 border border-brand-200 rounded">
                            <div className="text-xs font-medium text-slate-700 mb-2">Adjust Time per Person (15-minute intervals)</div>
                            {assignment.assignedPeople.map(personId => {
                              const person = allPeople.find(p => p.id === personId);
                              const { startTime, endTime } = getIndividualTimes(assignment, personId);

                              return (
                                <div key={personId} className="mb-4 p-2 bg-white border border-brand-100 rounded">
                                  <div className="text-xs font-medium text-brand-700 mb-2">
                                    {person?.first_name && person?.last_name
                                      ? `${person.last_name} ${person.first_name}`
                                      : person?.username || `Person ${personId}`}
                                  </div>
                                  <div className="mb-3">
                                    <label className="text-xs text-slate-600 block mb-1">Start Time: {startTime}</label>
                                    <input
                                      type="range"
                                      min={6 * 60}
                                      max={22 * 60}
                                      step={15}
                                      value={timeToMinutes(startTime)}
                                      onChange={(e) => {
                                        const newStartMinutes = parseInt(e.target.value);
                                        const newStartTime = minutesToTime(newStartMinutes);
                                        const currentEndMinutes = timeToMinutes(endTime);
                                        let newEndTime = endTime;
                                        if (newStartMinutes >= currentEndMinutes) {
                                          newEndTime = minutesToTime(newStartMinutes + 60);
                                        }
                                        resizeTaskForPerson(assignment, personId, newStartTime, newEndTime);
                                      }}
                                      className="w-full h-2 bg-white border border-steel-300 rounded-xl appearance-none cursor-pointer slider-thumb-black"
                                    />
                                  </div>
                                  <div className="mb-2">
                                    <label className="text-xs text-slate-600 block mb-1">End Time: {endTime}</label>
                                    <input
                                      type="range"
                                      min={7 * 60}
                                      max={23 * 60}
                                      step={15}
                                      value={timeToMinutes(endTime)}
                                      onChange={(e) => {
                                        const newEndMinutes = parseInt(e.target.value);
                                        const newEndTime = minutesToTime(newEndMinutes);
                                        const currentStartMinutes = timeToMinutes(startTime);
                                        if (newEndMinutes > currentStartMinutes) {
                                          resizeTaskForPerson(assignment, personId, startTime, newEndTime);
                                        }
                                      }}
                                      className="w-full h-2 bg-white border border-steel-300 rounded-xl appearance-none cursor-pointer slider-thumb-black"
                                    />
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    Duration: {(() => {
                                      const totalMinutes = timeToMinutes(endTime) - timeToMinutes(startTime);
                                      const hours = Math.floor(totalMinutes / 60);
                                      const minutes = totalMinutes % 60;
                                      return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
                                    })()}
                                  </div>
                                </div>
                              );
                            })}
                            <div className="flex justify-end">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowTimeSliders(null);
                                }}
                                className="text-brand-600 hover:text-brand-800 text-xs"
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        )}
                        {expandedTasks.has(assignment.id) && (
                          <div className="mt-2 pt-2 border-t border-steel-200">
                            <div className="text-xs font-medium text-slate-700 mb-1">Assigned People:</div>
                            <div
                              className="space-y-1 min-h-[20px] border-2 border-dashed border-steel-300 rounded p-2 transition-colors"
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.currentTarget.classList.add('border-brand-400', 'bg-brand-50');
                              }}
                              onDragLeave={(e) => {
                                e.currentTarget.classList.remove('border-brand-400', 'bg-brand-50');
                              }}
                              onDrop={async (e) => {
                                e.preventDefault();
                                e.currentTarget.classList.remove('border-brand-400', 'bg-brand-50');

                                try {
                                  const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                                  if (data.type === 'person' && data.taskId === assignment.id) {
                                    const updatedAssignment = {
                                      ...assignment,
                                      assigned_people: [...(assignment.assignedPeople || []), data.personId]
                                    };

                                    const token = localStorage.getItem('access_token');
                                    const headers = token ? { Authorization: `Bearer ${token}` } : {};

                                    await axios.put(`${API_BASE_URL}/api/task-assignments/${assignment.id}/`, {
                                      project: parseInt(selectedProject),
                                      turbine_id: assignment.turbineId,
                                      subcategory_id: assignment.subcategory_id, // Use subcategory_id (integer PK)
                                      start_time: assignment.startTime,
                                      end_time: assignment.endTime,
                                      people_needed: assignment.peopleNeeded,
                                      assigned_people: [...(assignment.assignedPeople || []), data.personId],
                                      assigned_car: assignment.assignedCar,
                                      is_alternative: assignment.isAlternative || false,
                                      date: assignment.date
                                    }, { headers });
                                    // Reload assignments
                                    await loadTaskAssignments();
                                  }
                                } catch (error) {
                                  console.error('Error adding person to task:', error);
                                }
                              }}
                            >
                              {assignment.assignedPeople && assignment.assignedPeople.length > 0 ? assignment.assignedPeople.map(personId => {
                                const person = allPeople.find(p => p.id === personId);
                                return (
                                  <div key={personId} className="text-xs text-slate-600 bg-steel-50 rounded px-2 py-1 flex items-center justify-between">
                                    <span className="font-semibold">
                                      {person ? (
                                        person.first_name && person.last_name
                                          ? `${person.last_name} ${person.first_name}`
                                          : person.username
                                      ) : `ID: ${personId}`}
                                    </span>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const person = allPeople.find(p => p.id === personId);
                                        const displayName = person?.first_name && person?.last_name
                                          ? `${person.last_name} ${person.first_name}`
                                          : person?.username || 'Unknown';

                                        if (!window.confirm(`Remove ${displayName} from this task?`)) {
                                          return;
                                        }

                                        try {
                                          const token = localStorage.getItem('access_token');
                                          const newAssignedPeople = assignment.assignedPeople.filter(id => id !== personId);

                                          // If this was the last person, delete the entire assignment
                                          if (newAssignedPeople.length === 0) {
                                            await axios.delete(
                                              `${API_BASE_URL}/api/task-assignments/${assignment.id}/`,
                                              { headers: { 'Authorization': `Bearer ${token}` } }
                                            );
                                            setTurbineAssignments(prev => ({
                                              ...prev,
                                              [assignment.turbineId]: prev[assignment.turbineId].filter(task => task.id !== assignment.id)
                                            }));
                                          } else {
                                            // Update with correct API field names (snake_case)
                                            const updatedAssignment = {
                                              project: parseInt(selectedProject),
                                              turbine_id: assignment.turbineId,
                                              subcategory_id: assignment.subcategory_id, // Use subcategory_id (integer PK)
                                              start_time: assignment.startTime,
                                              end_time: assignment.endTime,
                                              people_needed: assignment.peopleNeeded || 1,
                                              assigned_people: newAssignedPeople,
                                              assigned_car: assignment.assignedCar,
                                              is_alternative: assignment.isAlternative || false,
                                              date: assignment.date,
                                              individual_times: assignment.individual_times || {}
                                            };

                                            await axios.put(
                                              `${API_BASE_URL}/api/task-assignments/${assignment.id}/`,
                                              updatedAssignment,
                                              {
                                                headers: {
                                                  'Authorization': `Bearer ${token}`,
                                                  'Content-Type': 'application/json'
                                                }
                                              }
                                            );
                                            setTurbineAssignments(prev => {
                                              const updated = { ...prev };
                                              Object.keys(updated).forEach(tid => {
                                                updated[tid] = updated[tid].map(task =>
                                                  task.id === assignment.id
                                                    ? { ...task, assignedPeople: newAssignedPeople }
                                                    : task
                                                );
                                              });
                                              return updated;
                                            });
                                          }

                                          // Refresh all assignments to update experience modal
                                          if (loadAllProjectAssignments) {
                                            loadAllProjectAssignments();
                                          }
                                        } catch (error) {
                                          console.error('Error removing person from task:', error);
                                          alert('Failed to remove person from task');
                                        }
                                      }}
                                      className="ml-2 p-1 text-red-400 hover:text-red-600 rounded"
                                      title="Remove person"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                );
                              }) : (
                                <div className="text-xs text-slate-400 italic text-center py-2">
                                  Drag people here to assign them
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )})}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        }
      </div>
    </div>
  );
}

export default Timeline;